import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip';
import {
  TypographyCaption,
  TypographyFootNote,
  TypographyFootNoteBold,
} from '../typography';
import { type DismissibleTooltipProps } from './DismissibleTooltip.types';

/**
 * @deprecated Use `Spotlight` instead.
 */
export const DismissibleTooltip = ({
  children,
  title,
  description,
  learnMoreUrl,
  learnMoreText,
  isOpen,
  onOpenChange,
  shouldShow,
  openDelay = 600,
  dismissOnClickWithin,
  side = 'top',
  align = 'start',
  alignOffset = 20,
  sideOffset = 8,
}: DismissibleTooltipProps) => {
  const intl = useDesignSystemIntl();
  const closeLabel = intl.formatMessage({
    defaultMessage: 'Close tooltip',
    id: 'KyntLYt/jh',
    description: 'Default accessible label for the DismissibleTooltip close button',
  });

  useEffect(() => {
    if (!shouldShow) return;

    const timer = setTimeout(() => {
      onOpenChange(true);
    }, openDelay);

    return () => clearTimeout(timer);
  }, [shouldShow, openDelay, onOpenChange]);

  useEffect(() => {
    if (!isOpen || !dismissOnClickWithin) return;

    const handleClickAnywhere = (event: MouseEvent) => {
      const targetElement = document.querySelector(dismissOnClickWithin);

      if (targetElement && targetElement.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    document.addEventListener('click', handleClickAnywhere, true);

    return () => {
      document.removeEventListener('click', handleClickAnywhere, true);
    };
  }, [isOpen, dismissOnClickWithin, onOpenChange]);

  const handleDismiss = () => {
    if (isOpen) {
      onOpenChange(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip open={isOpen}>
        <TooltipTrigger asChild>
          <div onPointerDown={handleDismiss}>{children}</div>
        </TooltipTrigger>

        <TooltipContent
          side={side}
          align={align}
          alignOffset={alignOffset}
          sideOffset={sideOffset}
          className="max-w-[237px] gap-1 rounded-lg bg-background-primary py-2 pe-2 ps-3"
        >
          <div className="flex items-center justify-between">
            <TypographyCaption className="pb-1 text-text-primary-invert">
              {title}
            </TypographyCaption>
            <button
              onClick={handleDismiss}
              className="flex size-3 shrink-0 items-center justify-center rounded transition-colors hover:bg-text-primary-invert/10"
              aria-label={closeLabel}
            >
              <X className="size-3 text-text-primary-invert" strokeWidth={1.5} />
            </button>
          </div>
          <TypographyFootNote className="text-text-primary-invert/80">
            {description}
          </TypographyFootNote>
          {learnMoreUrl && learnMoreText && (
            <a
              href={learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDismiss}
              className="text-start"
              aria-label={`${learnMoreText} (opens in new tab)`}
            >
              <TypographyFootNoteBold className="pt-1 text-text-primary-invert">
                {learnMoreText}
              </TypographyFootNoteBold>
            </a>
          )}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
